import { useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { UserRole } from '@/generated/types';
import { useProject } from '@/pages/projects/hooks/useProject';
import { useInviteUser } from '@/pages/projects/hooks/useInviteUser';
import { UserSearchCombobox } from '@/components/shared/UserSearchCombobox';
import type { SearchUserResult } from '@/hooks/useSearchUsers';
import { ErrorPage, Paragraph, LoadingButton } from '@/components/shared';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function InviteUser() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const { project, loading, error } = useProject(id!);
  const { inviteUser, loading: inviteLoading, error: inviteError } = useInviteUser();

  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<SearchUserResult | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const canInviteByEmail = useMemo(
    () =>
      currentUser?.role === UserRole.Admin ||
      currentUser?.role === UserRole.Collaborator,
    [currentUser?.role]
  );

  const canManageMembers = useMemo(
    () =>
      !!(
        canInviteByEmail &&
        project?.members?.some((m) => m.id === currentUser?.id)
      ),
    [canInviteByEmail, project?.members, currentUser?.id]
  );

  const inviteEmail = selectedUser?.email ?? search.trim();

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!inviteEmail) {
      setFormError('Selecione um usuário ou informe um e-mail.');
      return;
    }

    if (!selectedUser && !EMAIL_PATTERN.test(inviteEmail)) {
      setFormError('Por favor, insira um endereço de e-mail válido.');
      return;
    }

    if (!selectedUser && !canInviteByEmail) {
      setFormError('Selecione um usuário existente para enviar o convite.');
      return;
    }

    try {
      await inviteUser(id!, inviteEmail);
      navigate(`/projects/${id}`);
    } catch (err) {
      console.error('Failed to invite user:', err);
    }
  };

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setFormError(null);
  }, []);

  if (error && !loading) return <ErrorPage message={`Erro: ${error.message}`} />;
  if (!project && !loading) return <ErrorPage message="Projeto não encontrado" />;
  if (!canManageMembers && !loading) {
    return (
      <ErrorPage message="Você não tem permissão para convidar usuários para este projeto" />
    );
  }

  return (
    <div className="min-h-screen bg-primary-50 dark:bg-primary-950 py-24">
      <div className="container-elegant max-w-2xl">
        <div className="mb-8">
          <Link
            to={`/projects/${id}`}
            className="text-sm uppercase tracking-widest text-primary-600 dark:text-primary-400 hover:text-primary-950 dark:hover:text-white mb-4"
          >
            ← Voltar ao Projeto
          </Link>
          <h1 className="text-3xl font-light tracking-tight text-primary-950 dark:text-white mb-2">
            Convidar Usuário
          </h1>
          <Paragraph>{project?.name}</Paragraph>
        </div>

        {inviteError && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>
              Erro ao enviar convite: {inviteError.message}
            </AlertDescription>
          </Alert>
        )}

        <Card>
          <CardContent className="p-8">
            <h3 className="text-lg font-light text-primary-950 dark:text-white mb-4">
              Buscar usuário
            </h3>
            <p className="text-primary-700 dark:text-primary-300 mb-6">
              Pesquise por nome ou e-mail. Se o usuário já estiver cadastrado, selecione-o na
              lista para adicioná-lo ao projeto imediatamente. Caso não exista, informe o e-mail
              para enviar um convite.
            </p>

            <form onSubmit={handleInvite} className="space-y-6">
              <UserSearchCombobox
                value={search}
                onChange={handleSearchChange}
                selectedUser={selectedUser}
                onSelectUser={setSelectedUser}
                excludeProjectId={id}
                label="Usuário"
                placeholder="Digite nome ou e-mail"
                error={formError ?? undefined}
                disabled={loading}
              />

              {!selectedUser && canInviteByEmail && search.trim().length >= 2 && (
                <p className="text-xs text-primary-600 dark:text-primary-400">
                  Usuário não encontrado? Confirme o e-mail acima e envie o convite — a pessoa
                  receberá instruções por e-mail para participar do projeto.
                </p>
              )}

              <div className="flex gap-4">
                <LoadingButton
                  type="submit"
                  loading={inviteLoading}
                  loadingText={selectedUser ? 'ADICIONANDO...' : 'ENVIANDO CONVITE...'}
                  disabled={!inviteEmail}
                  className="bg-primary-950 dark:bg-white text-white dark:text-primary-950 px-8 py-3 text-sm uppercase tracking-widest font-light hover:bg-primary-800 dark:hover:bg-primary-100 transition-colors duration-300"
                >
                  {selectedUser ? 'ADICIONAR AO PROJETO' : 'ENVIAR CONVITE'}
                </LoadingButton>
                <Button asChild variant="outline" size="lg">
                  <Link to={`/projects/${id}`}>CANCELAR</Link>
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card className="mt-8">
          <CardContent className="p-8">
            <h3 className="text-lg font-light text-primary-950 dark:text-white mb-4">
              Membros Atuais do Projeto
            </h3>
            <div className="space-y-3">
              {loading ? (
                Array.from({ length: 3 }).map((_, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between py-2 border-b border-primary-100 dark:border-primary-800 last:border-b-0"
                  >
                    <div className="space-y-2">
                      <Skeleton className="rounded w-32 h-4" />
                      <Skeleton className="rounded w-48 h-3" />
                      <Skeleton className="rounded w-20 h-3" />
                    </div>
                  </div>
                ))
              ) : (
                project?.members?.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center justify-between py-2 border-b border-primary-100 dark:border-primary-800 last:border-b-0"
                  >
                    <div>
                      <div className="text-primary-950 dark:text-white font-medium">
                        {member.fullName}
                      </div>
                      <div className="text-sm text-primary-600 dark:text-primary-400">
                        {member.email}
                      </div>
                      <div className="text-xs uppercase tracking-widest text-primary-500 dark:text-primary-500">
                        {member.role}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
